import { uuid } from "@jsplumbtoolkit/browser-ui";
import { API } from "@swivl-mono/api-helper";
import { CreateLogger, warn } from "@swivl-mono/helpers";
import { AppInterface, AppType, EscalationInterface, LocationVarKey, Log, SessionInterface, TwilioConferenceName, VarKey } from "@swivl-mono/types";
import { Call, Device } from "@twilio/voice-sdk";
import { set, setTime } from "react-datepicker/dist/date_utils";
import { create } from "zustand";

import { useLocationVariables } from "../../Hooks/useLocationVariables";
import AccountModel from "../Account/AccountModel";
import AppsModel from "../Apps/Apps.Model";
import { Member } from "../Member/Member.Current";
import MembersModel from "../Member/Members.Model";
import { UsersModel } from "../Users/Users.Model";
import {
  CallModelDeviceInit,
  CallModelFormatPhoneNumber,
  CallModelRegisterCallEvents,
  GetPhoneNumberForForEscalation,
} from "./MethodsAndInterfaces/Call.Model.Helpers";

const log = CreateLogger(Log.VOICE, "CallModel");

export enum CallStatus {
  // this extends  Call.State
  LOADING = "loading",
  IDLE = "idle",
  RINGING = "ringing",
  OPEN = "open",
  CLOSED = "closed",
  CONNECTING = "connecting",
  PENDING = "pending",
  RECONNECTING = "reconnecting",
}
export enum CallBarPosition {
  Hidden = "hidden",
  BottomRight = "bottom-right",
  BottomCenter = "bottom-center",
}

interface StoreInterface {
  escalationId?: number;
  escalationUserId?: number;
  escalationSessionId?: number;
  callBarPosition: CallBarPosition;

  isMuted: boolean;
  callStatus: CallStatus;
  callerNumber: string | null;
  callStartedAt?: Date;
}

const BlankState: StoreInterface = {
  escalationId: undefined,
  escalationUserId: undefined,
  callBarPosition: CallBarPosition.BottomRight,

  callStatus: CallStatus.LOADING,
  callerNumber: null,
  isMuted: false,
  callStartedAt: undefined,
};

export default class CallModel {
  static twilioApp: AppInterface;

  static calls: { [callSid: string]: Call } = {}; // This maps to our zustand state.

  static useState = create<StoreInterface>((set) => ({ ...BlankState }));
  static setState = CallModel.useState.setState;
  static get state() {
    return CallModel.useState.getState();
  }
  static reset() {
    CallModel.setState({ ...BlankState });
  }

  static twilioToken: string | null = null;
  static device: Device | null = null;
  static activeCall: Call | undefined = undefined;

  static async initializeDevice(accountTwilioTokenJWT: string): Promise<Device | null> {
    this.twilioApp = await API.Apps.getAppByTypeForLocation(AppType.TWILIO, Member.orgId!);

    if (this.twilioApp) {
      log("Found Twilio App", this.twilioApp);
    } else {
      warn("No Twilio app found, cannot initialize device");
      return null;
    }

    log("[CallModel] accountTwilioTokenJWT :", accountTwilioTokenJWT);

    if (!accountTwilioTokenJWT) {
      console.error("[CallModel] No token found");
      accountTwilioTokenJWT = await this.generateMemberTwilioToken(this.twilioApp, { force: true });
      if (!accountTwilioTokenJWT) {
        throw new Error("No token found");
      }
    }
    this.twilioToken = accountTwilioTokenJWT;

    CallModelDeviceInit(accountTwilioTokenJWT);

    await CallModel.device?.register().catch((error) => {
      console.error("[CallModel] Error registering device:", error);
      throw new Error("Failed to register device");
    });

    this.setState({ callStatus: CallStatus.IDLE });
    return this.device;
  }

  // static setCallerNumber(fromNumber: string) {
  //   this.setState({ callerNumber: fromNumber });
  //   log("[CallModel] Setting caller name for number:", fromNumber);
  // }

  // static acceptCall() {
  //   log("[CallModel] Accepting call");
  //   if (this.activeCall) {
  //     log("[CallModel] Accepted call ...", this.activeCall);
  //     this.activeCall.accept();
  //   }
  // }

  // static rejectCall() {
  //   log("[CallModel] Rejecting call");
  //   if (this.state.escalationId && UsersModel.state.escalations?.[this.state.escalationId]) {
  //     UsersModel.ignoreEscalation(UsersModel.state.escalations[this.state.escalationId]);
  //   }
  //   if (this.activeCall) {
  //     log("[CallModel] Rejected call ...");
  //     this.activeCall.reject();
  //     this.activeCall = undefined;
  //     // this.setStatus(CallStatus.IDLE);
  //   } else {
  //     log("[CallModel] No incoming connection to reject");
  //     // Fallback for demo/testing
  //     // this.setStatus(CallStatus.IDLE);
  //   }
  // }

  // static endCall() {
  //   if (this.state.escalationId && UsersModel.state.escalations?.[this.state.escalationId]) {
  //     const escalation = UsersModel.state.escalations?.[this.state.escalationId];
  //     const session = UsersModel.state.sessions?.[escalation.sessionId];
  //     if (session) {
  //       UsersModel.endTakeoverOfSession(session);
  //     }
  //   }
  //   log("[CallModel] Ending call");
  //   this.handleCallCleanup();
  // }

  static handleCallCleanup() {
    // In this case we may not even have an activeCall
    // but we still want to set to disconnecting so that we get a transition state
    if (this.activeCall) {
      log("[CallModel] Disconnecting call");
      this.activeCall.disconnect();
      this.activeCall = undefined;
    }
  }

  static toggleMute() {
    log("[CallModel] Attempting to toggle mute");
    if (this.activeCall) {
      if (this.state.isMuted) {
        log("[CallModel] Unmuting call");
        this.activeCall.mute(false);
        this.setState({ isMuted: false });
      } else {
        log("[CallModel] Muting call");
        this.activeCall.mute(true);
        this.setState({ isMuted: true });
      }
    } else {
      log("[CallModel] No active connection to toggle mute");
    }
  }

  static async generateMemberTwilioToken(app: AppInterface, options: { force?: boolean } = {}): Promise<string> {
    const swivlHasVoiceVar = await API.LocationVars.getVar(LocationVarKey.swivlHasVoice, Member.orgId!);
    if (!swivlHasVoiceVar || !swivlHasVoiceVar.length) {
      warn("[MembersModel] No swivlHasVoice location var found, skipping twilio token generation.");
      return Promise.resolve("");
    }
    console.log("swivlHasVoiceVar :", swivlHasVoiceVar);

    const swivlHasVoice = Boolean(Number(swivlHasVoiceVar));
    if (!swivlHasVoice) {
      return Promise.resolve("");
    }

    const { force = false } = options;
    if (CallModel.twilioToken && !force) {
      warn("[MembersModel] Twilio token already exists, skipping generation.");
      return Promise.resolve(CallModel.twilioToken);
    }
    return API.Members.generateMemberTwilioToken(Member.orgId!, Member.id!)
      .then((tokenData) => {
        if (!tokenData) {
          throw new Error("No token data");
        }
        let newMember = { ...AccountModel.state.member, twilioToken: tokenData } as any;
        log("tokenData :", tokenData);
        MembersModel.updateMember(newMember, true);
        CallModel.initializeDevice(tokenData);
        AccountModel.setState({ member: newMember });
        return tokenData;
      })
      .catch((error) => {
        warn("Error generating twilio token", error);
        throw error;
      });
  }

  static clearTwilioToken(): void {
    CallModel.twilioToken = null;
    API.Members.clearMemberTwilioToken(Member.orgId!, Member.id!)

    let newMember = { ...AccountModel.state.member, twilioToken: undefined } as any;
    MembersModel.updateMember(newMember, true);
    AccountModel.setState({ member: newMember });
  }
  static async startOutbound(escalation: EscalationInterface) {
    console.log("acceptedVoiceEscalation :", escalation, CallModel.device);
    const roomName = TwilioConferenceName(escalation.orgId, escalation.id);

    const fromNumber = this.twilioApp?.identifier;
    if (!fromNumber) {
      alert("No Twilio phone number configured, please contact support.");
      throw new Error("No Twilio phone number configured");
    }
    console.log("roomName :", roomName);
    // setTimeout(async () => {
    const call = await CallModel.device?.connect({
      params: {
        roomName: roomName,
        callUserInEscalation: String(escalation.id),
        fromNumber: fromNumber,
        toNumber: GetPhoneNumberForForEscalation(escalation, false),
      },
    });

    if (!call) {
      alert("There was an error starting the call, please try again.");
      throw new Error("No call object returned from Twilio");
    }
    this.setupCall(call, escalation);
  }

  static async acceptedVoiceEscalation(escalation: EscalationInterface) {
    // set the call bar position based on if our url includes "conversation/user/{escalation.userId}  "

    console.log("acceptedVoiceEscalation :", escalation, CallModel.device);
    const roomName = TwilioConferenceName(escalation.orgId, escalation.id);
    console.log("roomName :", roomName);
    const call = await CallModel.device?.connect({ params: { roomName: roomName } });

    if (!call) {
      alert("There was an error answering this call, please try again.");
      throw new Error("No call object returned from Twilio");
    }
    this.setupCall(call, escalation);
  }

  private static async setupCall(call: Call, escalation: EscalationInterface) {
    this.activeCall = call;
    let callBarPosition = CallBarPosition.BottomRight;

    if (window.location.href.includes(`conversations/user/${escalation.userId}`)) {
      callBarPosition = CallBarPosition.BottomCenter;
    }

    this.setState({
      escalationId: escalation.id,
      escalationSessionId: escalation.sessionId,
      escalationUserId: escalation.userId,
      callerNumber: GetPhoneNumberForForEscalation(escalation, true),
      callStartedAt: new Date(),
      isMuted: false,
      callBarPosition: callBarPosition,
    });
    await CallModelRegisterCallEvents(this.activeCall);
  }

  static async ignoreVoiceEscalation(escalation: EscalationInterface) {
    console.log("Ignoring voice escalation");
    // We probably dont need to do anything here.
  }
  static async endVoiceCallForSession(session: SessionInterface) {
    console.log("Ending voice call for session");
    if (this.state.escalationSessionId !== session.id) {
      console.log("Session does not match active escalation session, skipping...");
      return;
    }
    if (this.activeCall) {
      this.activeCall.disconnect();
      this.activeCall = undefined;
    }
    this.setState({
      escalationId: undefined,
      escalationSessionId: undefined,
      escalationUserId: undefined,
      callBarPosition: CallBarPosition.Hidden,
      callStatus: CallStatus.IDLE,
      isMuted: false,
      callerNumber: undefined,
      callStartedAt: undefined,
    });
  }
}
